import { useMemo } from 'react';

import { Select } from '@/lib/base/forms/select/select';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import { COMMON_TIMEZONES, getTimezoneOffset } from '@/lib/scheduling/timezone';

import type { PropertyInputProps } from './TextInput';

export const TimezoneInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  inputRef,
  placeholder,
  autoOpenPicker,
  size = 'sm',
}) => {
  const items = useMemo(
    () =>
      COMMON_TIMEZONES.map(tz => ({
        id: tz.value,
        label: tz.label,
        supportingText: getTimezoneOffset(tz.value),
      })),
    [] // COMMON_TIMEZONES is static, no dependencies needed
  );

  const Icon = getPropertyTypeIconComponent(propertyType);

  return (
    <Select.ComboBox
      placeholder={placeholder || 'Select timezone'}
      // Table-cell editing renders at the compact `xs` scale so the (often
      // long) selected timezone label matches the table's value type (text-sm)
      // instead of the caller's size (default `sm`, the roomier standalone
      // form-field treatment).
      size={autoOpenPicker ? 'xs' : size}
      items={items}
      selectedKey={value !== null && value !== undefined ? String(value) : null}
      onSelectionChange={key =>
        onChange(key !== null && key !== undefined ? String(key) : null)
      }
      isDisabled={disabled}
      shortcut={false}
      aria-label={propertyType.label}
      icon={Icon}
      ref={inputRef}
    >
      {item => (
        <Select.Item id={item.id} textValue={item.label}>
          {item.label}
          {item.supportingText && (
            <span className='text-text-tertiary ml-2 text-xs'>
              {item.supportingText}
            </span>
          )}
        </Select.Item>
      )}
    </Select.ComboBox>
  );
};
